import { Card, cn } from '@up2stay/up2promo-ui';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

type ProductsSetupChoiceCardProps = {
  title: string;
  description?: string;
  icon?: LucideIcon;
  leading?: ReactNode;
  onClick: () => void;
  /** Visual emphasis for the recommended path — still same footprint as siblings. */
  emphasized?: boolean;
  className?: string;
};

/**
 * Equal-weight setup choice using the shared Card surface radius
 * (`--radius-card` / Card), not nested tokens.
 */
export function ProductsSetupChoiceCard({
  title,
  description,
  icon: Icon,
  leading,
  onClick,
  emphasized = false,
  className,
}: ProductsSetupChoiceCardProps) {
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'flex w-full cursor-pointer items-start gap-3 px-3.5 py-2.5 text-left transition-[border-color,background-color,box-shadow] sm:gap-4 sm:px-5 sm:py-4',
        emphasized
          ? 'border-primary/45 bg-primary/[0.06] shadow-sm ring-1 ring-primary/15'
          : 'bg-card/80 hover:border-border hover:bg-muted/40',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-nested)] sm:size-10',
          emphasized
            ? 'bg-primary/15 text-primary'
            : 'bg-muted/70 text-muted-foreground',
        )}
      >
        {leading ??
          (Icon ? <Icon className="size-4 sm:size-5" aria-hidden /> : null)}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[14px] font-semibold leading-snug text-foreground sm:text-[15px]">
          {title}
        </span>
        {description ? (
          <span className="text-[12.5px] leading-snug text-muted-foreground sm:text-[13.5px] sm:leading-relaxed">
            {description}
          </span>
        ) : null}
      </span>
    </Card>
  );
}
